import useGiphyDialog from '@tenant/recognition/RecognitionModal/sendRecognitionView/giphySearch/useGiphyDialog'
import FroalaEditorGlobal from 'froala-editor'
import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

let isGifButtonRegistered = false

declare global {
  interface Window {
    FroalaEditor?: {
      DefineIcon: (
        name: string,
        options: { NAME?: string; SVG_KEY?: string; template?: string; PATH?: string },
      ) => void
      DefineIconTemplate: (name: string, template: string) => void
      RegisterCommand: (
        name: string,
        options: {
          title: string
          icon: string
          undo: boolean
          focus: boolean
          showOnMobile: boolean
          refreshAfterCallback: boolean
          callback: () => void
        },
      ) => void
    }
    __froalaGifButtonCallback?: () => void
  }
}

const registerGifButtonCommand = () => {
  if (isGifButtonRegistered) return

  FroalaEditorGlobal.DefineIconTemplate(
    'svgCentered',
    '<svg viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg"><g transform="translate(3, 3) scale(1.3333)"><path d="[PATH]"/></g></svg>',
  )

  FroalaEditorGlobal.DefineIcon('insertGifIcon', {
    PATH: 'M8.75 12C8.96667 12 9.14583 11.9292 9.2875 11.7875C9.42917 11.6458 9.5 11.4667 9.5 11.25V6.75C9.5 6.53333 9.42917 6.35417 9.2875 6.2125C9.14583 6.07083 8.96667 6 8.75 6C8.53333 6 8.35417 6.07083 8.2125 6.2125C8.07083 6.35417 8 6.53333 8 6.75V11.25C8 11.4667 8.07083 11.6458 8.2125 11.7875C8.35417 11.9292 8.53333 12 8.75 12ZM4 12H6C6.28333 12 6.52083 11.9042 6.7125 11.7125C6.90417 11.5208 7 11.2833 7 11V9.75C7 9.53333 6.92917 9.35417 6.7875 9.2125C6.64583 9.07083 6.46667 9 6.25 9C6.03333 9 5.85417 9.07083 5.7125 9.2125C5.57083 9.35417 5.5 9.53333 5.5 9.75V10.5H4.5V7.5H6.25C6.46667 7.5 6.64583 7.42917 6.7875 7.2875C6.92917 7.14583 7 6.96667 7 6.75C7 6.53333 6.92917 6.35417 6.7875 6.2125C6.64583 6.07083 6.46667 6 6.25 6H4C3.71667 6 3.47917 6.09583 3.2875 6.2875C3.09583 6.47917 3 6.71667 3 7V11C3 11.2833 3.09583 11.5208 3.2875 11.7125C3.47917 11.9042 3.71667 12 4 12ZM11.75 12C11.9667 12 12.1458 11.9292 12.2875 11.7875C12.4292 11.6458 12.5 11.4667 12.5 11.25V10H13.75C13.9667 10 14.1458 9.92917 14.2875 9.7875C14.4292 9.64583 14.5 9.46667 14.5 9.25C14.5 9.03333 14.4292 8.85417 14.2875 8.7125C14.1458 8.57083 13.9667 8.5 13.75 8.5H12.5V7.5H14.75C14.9667 7.5 15.1458 7.42917 15.2875 7.2875C15.4292 7.14583 15.5 6.96667 15.5 6.75C15.5 6.53333 15.4292 6.35417 15.2875 6.2125C15.1458 6.07083 14.9667 6 14.75 6H11.75C11.5333 6 11.3542 6.07083 11.2125 6.2125C11.0708 6.35417 11 6.53333 11 6.75V11.25C11 11.4667 11.0708 11.6458 11.2125 11.7875C11.3542 11.9292 11.5333 12 11.75 12ZM2 18C1.45 18 0.979167 17.8042 0.5875 17.4125C0.195833 17.0208 0 16.55 0 16V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H16C16.55 0 17.0208 0.195833 17.4125 0.5875C17.8042 0.979167 18 1.45 18 2V16C18 16.55 17.8042 17.0208 17.4125 17.4125C17.0208 17.8042 16.55 18 16 18H2ZM2 16H16V2H2V16Z',
    template: 'svgCentered',
  })

  FroalaEditorGlobal.RegisterCommand('insertGif', {
    title: 'Insert GIF',
    icon: 'insertGifIcon',
    undo: true,
    focus: true,
    showOnMobile: true,
    refreshAfterCallback: true,
    callback: function () {
      if (typeof window !== 'undefined' && window.__froalaGifButtonCallback) {
        window.__froalaGifButtonCallback()
      }
    },
  })

  isGifButtonRegistered = true
}

registerGifButtonCommand()

type UseFroalaGifsProps = {
  fieldName?: string
  modalId?: string
}

export const useFroalaGifs = ({
  fieldName = 'gif',
  modalId = 'gifModal',
}: UseFroalaGifsProps = {}) => {
  const giphyDialogId = useMemo(
    () => `rich-text-editor-${fieldName}-${modalId}`,
    [fieldName, modalId],
  )
  const { openGiphyDialog } = useGiphyDialog(giphyDialogId)
  const { setValue } = useFormContext()

  useEffect(() => {
    registerGifButtonCommand()

    const timeout1 = setTimeout(registerGifButtonCommand, 100)
    const timeout2 = setTimeout(registerGifButtonCommand, 500)

    return () => {
      clearTimeout(timeout1)
      clearTimeout(timeout2)
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__froalaGifButtonCallback = openGiphyDialog
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.__froalaGifButtonCallback = undefined
      }
    }
  }, [openGiphyDialog])

  const handleGifSelected = (url: string) => {
    // Set the GIF URL in the form field instead of inserting into the editor
    setValue(fieldName, url, { shouldValidate: true, shouldDirty: true })
  }

  return {
    giphyDialogId,
    handleGifSelected,
    registerGifButtonCommand,
  }
}

export default useFroalaGifs
